import apiCache from './apiCache';
import {
  usStates,
  canadianProvinces,
  indianStates,
  frenchGuianaDepartments,
  frenchPolynesiaSubdivisions,
} from '../constants/ccBill';
import { tzToCountry } from '../constants/timezoneToCountry';
import { CLOUDFLARE_LOCATION } from '../constants/common';
import { getLocalItem, setLocalItem, removeLocalItem } from './config/localStorage';
import { reportError } from '../sentry';

const COUNTRY_STATE_MAP = {
  US: usStates,
  CA: canadianProvinces,
  IN: indianStates,
  GF: frenchGuianaDepartments,
  PF: frenchPolynesiaSubdivisions,
};

const COUNTRY_CODE_CACHE_KEY = 'countryCode';
const COUNTRY_CODE_CACHE_EXPIRY_KEY = 'countryCodeExpiry';
const CACHE_DURATION_MS = 6 * 60 * 60 * 1000; // 6 hours in milliseconds

export const getStatesForCountry = (countryCode) => {
  return COUNTRY_STATE_MAP[countryCode] || [];
};

/**
 * Gets cached country code from localStorage if still valid
 * @returns {string|null} Cached country code or null if expired/not found
 */
const getCachedCountryCode = () => {
  try {
    const cachedCode = getLocalItem(COUNTRY_CODE_CACHE_KEY);
    const expiryTime = getLocalItem(COUNTRY_CODE_CACHE_EXPIRY_KEY);

    if (!cachedCode || !expiryTime) {
      return null;
    }

    const now = Date.now();
    if (now > expiryTime) {
      // Cache expired, remove it
      removeLocalItem(COUNTRY_CODE_CACHE_KEY);
      removeLocalItem(COUNTRY_CODE_CACHE_EXPIRY_KEY);
      return null;
    }

    return cachedCode;
  } catch (error) {
    return null;
  }
};

/**
 * Sets country code in localStorage with 6-hour expiration
 * @param {string} countryCode - Country code to cache
 */
const setCachedCountryCode = (countryCode) => {
  try {
    const expiryTime = Date.now() + CACHE_DURATION_MS;
    setLocalItem(COUNTRY_CODE_CACHE_KEY, countryCode);
    setLocalItem(COUNTRY_CODE_CACHE_EXPIRY_KEY, expiryTime);
  } catch (error) {
    // Silently fail if localStorage is not available
  }
};

/**
 * Normalizes a string by converting special characters (diacritics) to their ASCII equivalents
 * Example: "Ghāziābād" → "Ghaziabad"
 * The "ā" character is a Latin letter 'a' with a macron (diacritic mark)
 * @param {string} str - The string to normalize
 * @returns {string} - Normalized string with ASCII characters only
 */
const normalizeString = (str) => {
  if (!str || typeof str !== 'string') {
    return str;
  }

  // Normalize Unicode characters (NFD = Normalization Form Decomposed)
  // This separates base characters from their diacritical marks
  // Then remove all combining diacritical marks (Unicode range \u0300-\u036F)
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '');
};

const parseLocationData = (data, source) => {
  if (source === 'cloudflare') {
    return {
      service: 'cloudflare',
      ip: data.ip,
      latitude: data.latitude,
      longitude: data.longitude,
      postalCode: data.postalCode,
      country: data.country,
      state: data.region,
      stateCode: data.regionCode,
      city: normalizeString(data.city),
      success: true
    };
  }
  
  if (source === 'ipapi') {
    if (data.country_code && data.region_code) {
      return {
        service: 'ipapi',
        country: data.country_code.toUpperCase(),
        state: data.region_code.toUpperCase(),
        city: data.city,
        success: true
      };
    }
  } 
  
  if (source === 'ipapi-com') {
    return {
      service: 'ipapi-com',
      country: data.countryCode.toUpperCase(),
      state: data.regionCode.toUpperCase(),
      city: data.city,
      success: true
    };
  }
  
  return null;
};

const getLocationFromCloudflare = async () => {
  try {
    const response = await fetch(CLOUDFLARE_LOCATION.URL, {
      headers: {
        "CF-Access-Client-Id": CLOUDFLARE_LOCATION.ACCESS_CLIENT_ID
      },
    });

    if (response?.ok && response?.status === 200) {
      const data = await response.json();
      return parseLocationData(data, 'cloudflare');
    }
    
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Fetches user location from ipapi.co service
 * @returns {Promise<Object|null>} Location data object or null if failed
 */
const getLocationFromIpapi = async () => {
  try {
    const response = await fetch('https://ipapi.co/json/');

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return parseLocationData(data, 'ipapi');
  } catch (error) {
    return null;
  }
};

/**
 * Fetches user location from ip-api.com service
 * @returns {Promise<Object|null>} Location data object or null if failed
 */
const getLocationFromIpapiCom = async () => {
  try {
    const ipAddress = await apiCache.getIPAddress();

    if (!ipAddress) {
      return null;
    }

    const locationResponse = await fetch(`https://ip-api.com/json/${ipAddress}`);

    if (!locationResponse.ok) {
      return null;
    }

    const locationData = await locationResponse.json();
    return parseLocationData(locationData, 'ipapi-com');
  } catch (error) {
    return null;
  }
};

export const getUserLocation = async () => {
  try {
    // Try cloudflare first
    const cloudflareResult = await getLocationFromCloudflare();
    if (cloudflareResult) {
      return cloudflareResult;
    }

    // Try ipapi.co as fallback
    const ipapiResult = await getLocationFromIpapi();
    if (ipapiResult) {
      return ipapiResult;
    }

    // Try ip-api.com as fallback
    const ipapiComResult = await getLocationFromIpapiCom();
    if (ipapiComResult) {
      return ipapiComResult;
    }

    return { success: false };
  } catch (error) {
    reportError(error, 'getUserLocation');
    return { success: false };
  }
};

/**
 * Gets country code from user location or timezone fallback
 * Uses localStorage cache with 6-hour expiration to avoid repeated API calls
 * @returns {Promise<string|null>} Country code or null if not found
 */
export const getCountryCode = async () => {
  // Check cache first
  const cachedCode = getCachedCountryCode();
  if (cachedCode) {
    return cachedCode;
  }

  // Cache miss or expired, fetch fresh data
  let countryCode = null;
  const location = await getUserLocation();
  
  if (location?.success && location?.country) {
    countryCode = location.country;
  } else {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) {
      countryCode = tzToCountry[tz] || null;
    }
  }

  // Cache the result if we got a valid country code
  if (countryCode) {
    setCachedCountryCode(countryCode);
  }

  return countryCode || null;
};